import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Presale, PresaleCode, validatePresaleSubmission } from '../../api/presaleTypes';
import { FOCUS_RING } from '../components/styles';

const button = `rounded-lg border border-white/15 px-3 py-2 text-sm text-white hover:bg-white/10 disabled:opacity-50 ${FOCUS_RING}`;
const input = `mt-1 w-full rounded-lg border border-white/15 bg-black/20 px-3 py-2 text-white ${FOCUS_RING}`;

export function usePresaleCodes(identity: string) {
  return useQuery({
    queryKey: ['presale-codes', identity],
    queryFn: api.getPresales,
    enabled: Boolean(identity),
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
}

function CodeRow({ code, sourceUrl, refresh, canDelete, onDeleted }: { code: PresaleCode; sourceUrl: string; refresh: () => Promise<unknown>; canDelete: boolean; onDeleted: () => void }) {
  const deleting = useRef(false);
  const [needsReconcile, setNeedsReconcile] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [vote, setVote] = useState({ upvote_count: code.upvote_count, viewer_has_upvoted: code.viewer_has_upvoted });
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  async function castVote() {
    setPending(true);
    setMessage('');
    try { setVote(await api.votePresaleCode(code.id, vote.viewer_has_upvoted)); }
    catch (error) { setMessage((error as Error).message); }
    finally { setPending(false); }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(code.code || '');
      setMessage('');
      setCopied(true);
    } catch { setMessage('Could not copy. Select the code above and copy it manually.'); }
  }
  async function reconcile() {
    setPending(true);
    try { await refresh(); setNeedsReconcile(false); }
    catch { setNeedsReconcile(true); setMessage('Could not refresh codes. Refresh before retrying deletion.'); }
    finally { setPending(false); }
  }
  async function remove() {
    if (!canDelete || deleting.current || needsReconcile) return;
    deleting.current = true;
    setPending(true);
    setMessage('');
    try {
      await api.deletePresaleCode(code.id);
      setDeleted(true);
      onDeleted();
      try { await refresh(); }
      catch { setNeedsReconcile(true); setMessage('Code deleted, but the list could not refresh.'); }
    } catch (error) {
      const status = (error as { status?: number }).status;
      setMessage(status === 401 ? 'Deletion was refused. Check your sign-in; if you are signed in, the backend admin key may need attention.'
        : status === 403 ? 'Permission denied. Only superusers can delete community codes.'
        : status === 503 ? 'Deletion service unavailable. The code has been kept until the list is refreshed.'
        : (error as Error).message);
      if (status === undefined || status === 404 || status === 503 || status === 504) {
        setNeedsReconcile(true);
        try { await refresh(); setNeedsReconcile(false); }
        catch { setMessage('Deletion could not be reconciled. Refresh codes before retrying.'); }
      }
    } finally { deleting.current = false; setPending(false); }
  }
  const refreshButton = needsReconcile && <button type="button" className={button} disabled={pending} onClick={reconcile}>Refresh codes</button>;
  if (deleted) return <div className="text-xs text-white/50">Code deleted.{message && <p role="alert">{message}</p>}{refreshButton}</div>;
  const safeUrl = /^https?:\/\//i.test(sourceUrl) ? sourceUrl : null;
  const label = code.label?.trim();
  const showLabel = label && label.toLowerCase() !== 'user submitted';
  const compactAction = `rounded-md px-2 py-1 text-xs text-white/60 hover:bg-white/5 hover:text-white disabled:opacity-50 ${FOCUS_RING}`;
  return <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
    {showLabel && <p className="mb-1 text-xs text-white/50">{label}</p>}
    <div className="flex items-center gap-2">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {code.code_type === 'shared' && code.code && <>
          <span className="break-all py-1 font-sans text-sm font-medium text-emerald-400 select-all">{code.code}</span>
          <button type="button" className={compactAction} onClick={copy}>{copied ? 'Copied' : 'Copy code'}</button>
          {safeUrl && <a className={compactAction} href={safeUrl} target="_blank" rel="noopener noreferrer">Open ticket site ↗</a>}
        </>}
      </div>
      <button type="button"
        className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors disabled:opacity-50 ${FOCUS_RING} ${vote.viewer_has_upvoted ? 'bg-blue-500/10 text-blue-300' : 'text-white/50 hover:bg-white/5 hover:text-white'}`}
        aria-label={`${vote.viewer_has_upvoted ? 'Remove upvote' : 'Upvote'} · ${vote.upvote_count}`}
        title={vote.viewer_has_upvoted ? 'Remove upvote' : 'Upvote'}
        aria-pressed={vote.viewer_has_upvoted} disabled={pending} onClick={castVote}>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
          <path d="M7 10v11H3V10h4Zm0 0 5-8a3 3 0 0 1 3 3v4h4a2 2 0 0 1 2 2.4l-1.5 7A3 3 0 0 1 16.6 21H7" />
        </svg>
        <span className="tabular-nums">{vote.upvote_count}</span>
      </button>
      {canDelete && <button type="button"
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/35 transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50 ${FOCUS_RING}`}
        aria-label="Delete code" title="Delete code" aria-expanded={confirmDelete}
        disabled={pending} onClick={() => setConfirmDelete(value => !value)}>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
          <path d="M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6" />
        </svg>
      </button>}
    </div>
    {canDelete && confirmDelete && <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-red-400/15 bg-red-500/5 px-3 py-2">
        <span className="text-xs text-white/60">Delete “{code.code || code.label || `Code #${code.id}`}”? It will be removed for everyone.</span>
        <button type="button" className={`rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-300 transition-colors hover:bg-red-500/20 disabled:opacity-50 ${FOCUS_RING}`} disabled={pending || needsReconcile} onClick={remove}>Confirm delete</button>
        <button type="button" className={compactAction} disabled={pending} onClick={() => setConfirmDelete(false)}>Cancel</button>
    </div>}
    {code.code_type === 'per_user' && <p className="mt-2 text-sm text-amber-200">Personal code — follow the instructions to get your own.</p>}
    {code.code_type === 'tba' && <p className="mt-2 text-sm text-white/60">Code pending announcement</p>}
    {code.instructions && <p className="mt-2 whitespace-pre-wrap break-words text-sm text-white/70">{code.instructions}</p>}
    {refreshButton}
    {message && <p role="status" className="mt-2 text-sm text-blue-200">{message}</p>}
  </div>;
}

export function PresaleCodes({ eventId, sourceUrl, presales, loading, error, refresh, canDelete = false }: {
  canDelete?: boolean;
  eventId: number; sourceUrl: string; presales: Presale[]; loading: boolean; error: Error | null;
  refresh: () => Promise<unknown>;
}) {
  const eventPresales = presales.filter(p => p.event.id === eventId);
  const eventCodes = eventPresales.flatMap(p => p.codes || []);
  const [codesExpanded, setCodesExpanded] = useState(false);
  const visibleCodes = codesExpanded ? eventCodes : eventCodes.slice(0, 3);
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [instructions, setInstructions] = useState('');
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [needsRefresh, setNeedsRefresh] = useState(false);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const payload = { code, label, instructions };
    const invalid = validatePresaleSubmission(payload);
    if (invalid) { setMessage(invalid); return; }
    setPending(true);
    setMessage('');
    try {
      const result = await api.submitEventPresaleCode(eventId, payload);
      setMessage(result.was_duplicate ? 'This code was already shared. Your contribution was recorded.' : 'Thanks! Your code was contributed.');
      setCode(''); setLabel(''); setInstructions('');
      try { await refresh(); } catch { setMessage('Your contribution was recorded, but codes could not refresh. Refresh to see the latest list.'); setNeedsRefresh(true); }
    } catch (err) {
      const status = (err as { status?: number }).status;
      const uncertain = status === undefined || status === 503 || status === 504;
      setMessage(uncertain ? 'Submission outcome is uncertain. Check the refreshed codes before manually submitting again.' : (err as Error).message);
      if (uncertain) {
        setNeedsRefresh(true);
        try { await refresh(); setNeedsRefresh(false); } catch { /* Keep submission blocked until a successful refresh. */ }
      }
    } finally { setPending(false); }
  }
  return <section className="mt-4 border-t border-white/10 pt-4" aria-label="Presale codes">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-white">Community codes</h3>
      <button type="button"
        className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white ${FOCUS_RING}`}
        aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-45' : ''}`}>
          <path d="M8 3v10M3 8h10" />
        </svg>
        {expanded ? 'Close contribution' : 'Contribute code'}
      </button>
    </div>
    <p className="mt-1 text-xs text-white/40">Sale timing and upvotes do not guarantee a code will work.</p>
    {loading ? <p className="mt-3 text-sm text-white/60">Loading codes…</p> : error && eventCodes.length === 0 ? <p role="alert" className="mt-3 text-sm text-amber-200">Codes unavailable: {error.message}</p> : <div className="mt-3 space-y-2">
      {visibleCodes.map(c => <CodeRow key={`${c.id}:${c.upvote_count}:${c.viewer_has_upvoted}`} code={c} sourceUrl={sourceUrl} refresh={refresh} canDelete={canDelete} onDeleted={() => setMessage('Code deleted.')} />)}
      {eventCodes.length > 3 && <button type="button" className={button} aria-expanded={codesExpanded} onClick={() => setCodesExpanded(value => !value)}>
        {codesExpanded ? 'Show fewer codes' : `Show all ${eventCodes.length} codes`}
      </button>}
      {!eventPresales.some(p => p.codes?.length) && <p className="text-sm text-white/50">No codes in the loaded results. Have one? Share it here.</p>}
    </div>}
    {error && eventCodes.length > 0 && <p role="alert" className="mt-2 text-sm text-amber-200">Could not refresh codes: {error.message}</p>}
    {(error || needsRefresh) && <button type="button" className={`${button} mt-2`} disabled={pending} onClick={async () => {
      setPending(true);
      try { await refresh(); setNeedsRefresh(false); } catch { setMessage('Refresh failed. Try again before submitting.'); }
      finally { setPending(false); }
    }}>Refresh codes</button>}
    {expanded && <form onSubmit={submit} className="mt-4 space-y-3 rounded-xl bg-white/[0.03] p-4">
      <label className="block text-sm text-white/70">Code <input required className={input} value={code} onChange={e => setCode(e.target.value)} autoComplete="off" /></label>
      <label className="block text-sm text-white/70">Label (optional)<input className={input} placeholder="Artist presale" value={label} onChange={e => setLabel(e.target.value)} /></label>
      <label className="block text-sm text-white/70">Instructions (optional)<textarea className={input} placeholder="Where and how to use this code" value={instructions} onChange={e => setInstructions(e.target.value)} /></label>
      <p className="text-xs text-white/50">Share reusable codes only. Your code and instructions will be visible to other users. Limit: 10 submissions per sale per hour, including duplicates.</p>
      <button type="submit" className={`${button} bg-blue-600/30`} disabled={pending || needsRefresh}>{pending ? 'Submitting…' : 'Submit code'}</button>
    </form>}
    {message && <p role="status" className="mt-3 text-sm text-blue-200">{message}</p>}
  </section>;
}

/** Read permissions independently per signed-in identity; never reuse another user's profile. */
export function usePresaleModerator(identity: string) {
  return useQuery({ queryKey: ['presale-moderator', identity], queryFn: api.getProfile,
    enabled: Boolean(identity), gcTime: 0, staleTime: 0, retry: false });
}
